import React from "react";

import { Typography } from "@material-ui/core";

import {
  AddAllIcon,
  AddIcon,
  DocumentIcon,
  EditIcon,
  InfoOutlinedIcon,
  InvoiceIcon,
  LinkOffIcon,
  RemoveIcon,
} from "../../../components/common/iconography";

export const EditHistoryLogHelp = () => (
  <>
    <Typography variant="overline">
      <strong>History Log</strong>
    </Typography>
    <Typography variant="subtitle1">
      The History Tab provides an automated, chronological Audit Trail of key
      events and record changes.
    </Typography>
  </>
);

export const EditInboundSetupHelp = ({ classes, receiptData }) => (
  <>
    <Typography variant="overline">
      <strong>Inbound Setup</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Origin: </strong>
          Where the shipment is coming from. If shipped directly from customer,
          flip the "Copy Account" switch below the field.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Commodity: </strong>
          Select commodity category. Drives default units of measure and other
          settings.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Carrier Type: </strong>
          Select load type - If "Truckload", the "Railcar Number" field will
          change to "Trailer Number".
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Origin Carrier: </strong>
          Name of Railroad or Trucking company delivering the shipment.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Railcar / Trailer Number: </strong>
          Railcar Number if inbound by Rail, Trailer Number if inbound by Truck.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>PO Number: </strong>
          Purchase Order Number provided by Customer. Used for auto-generating
          Lot Number.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Area and Zone: </strong>
          Select storage destination for inbound commodity. Location will be set
          at a later stage.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Lot Type: </strong> Used to auto-generate Lot Number.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Lot Number: </strong>
          Lot Number Prefix is auto-populated for selected Account.
          Auto-generated using either the PO Number for Car Lot loads, or
          current date for Perpetual loads. Lot Number, excluding the prefix,
          can be manually customized by selecting the "Manual Override" switch
          beneath the field.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>PO File: </strong>
          Attach the original PO from customer.
        </Typography>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Custom Fields</strong>
        </Typography>
        <Typography>
          User-defined in Account setup. These fields are Account-specific, so
          they may differ from one inbound to another.
        </Typography>
      </li>
      <li>
        <Typography>
          <InvoiceIcon className={classes.icon} />
          <strong> INVOICE: </strong>Generate new Invoice for this Inbound
          Shipment.
        </Typography>
      </li>
      <li>
        <Typography>
          <DocumentIcon className={classes.icon} />
          <strong> RECEIPT: </strong>
          Generate Bill of Lading as a downloadable, editable .DOCX document.
          Custom Receipt template can up uploaded in Account settings.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Receipt Data: </strong>
        </Typography>
        <DocData obj={receiptData} />
      </li>
    </ul>
  </>
);

export const EditTallySheetLogHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Tally Sheet</strong>
    </Typography>
    <Typography variant="subtitle1">
      Completing the Tally Sheet Tab also creates new Inventory records, which
      will appear in the Inventory List once the Shipment is "Accepted" and
      "Stored".
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Product Name: </strong>
          Provide full product name and description. Eg. "2.4.08 2&BTR WSPF 294
          PC" for 2x4x8 2&Better WSPF 294 Pieces. Items selected from Product
          Catalog will have an <InfoOutlinedIcon
            className={classes.icon}
          />{" "}
          Info Bubble in the right corner. Those not linked to a Product, will
          display <LinkOffIcon className={classes.icon} /> broken link icon.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Qty. Advised: </strong>
          Quantity of product to be received - Unit count.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Qty. Received: </strong>
          Field becomes editable once the shipment is marked "Placed". Qty.
          Received should equal Qty. Advised if no issues with shipment. If
          discrepancy exists, contact customer and adjust either Qty. Advised or
          Received to resolve.
        </Typography>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Custom Fields</strong>
        </Typography>
        <Typography>
          User-defined in Account setup or Product Catalog. These fields are
          Account- and Product-specific, so they may differ from one inbound to
          another. The Custom Fields button allows you to overrride default
          show/hide settings in the Account setup.
        </Typography>
      </li>
      <li>
        <Typography>
          <AddIcon className={classes.icon} />
          <strong> ADD: </strong>
          Add a custom Line Item to Tally Sheet - not linked to Product Catalog.
        </Typography>
      </li>
      <li>
        <Typography>
          <AddAllIcon className={classes.icon} />
          <strong> CATALOG ADD: </strong>
          Search Product Catalog to add a new Line Item to Tally Sheet.
        </Typography>
      </li>
      <li>
        <Typography>
          <RemoveIcon className={classes.icon} />
          <strong> REMOVE: </strong>
          Remove selected Tally Sheet Line Item.
        </Typography>
      </li>
      <li>
        <Typography>
          <EditIcon className={classes.icon} />
          <strong> EDIT: </strong>
          Edit Qty. Advised / Received of selected Line Item. After saving or
          cancelling changes, you will be brought back to this screen.
        </Typography>
      </li>
      <li>
        <Typography gutterBottom>
          <strong>Total Quantity & Shipping Units: </strong>
          Sum total of Qty. Advised. Indicate units of measure for counting Qty
          Advised / Received.
        </Typography>
      </li>
      <li>
        <Typography gutterBottom>
          <strong>Bill Quantity & Units: </strong>
          Total Qty. Advised and Units used for invoicing customer.
        </Typography>
      </li>
    </ul>
  </>
);

export const EditDispatchLogHelp = () => (
  <>
    <Typography variant="overline">
      <strong>Dispatch</strong>
    </Typography>
    <Typography variant="subtitle1">
      Use the Dispatch Tab to capture key event timestamps when the shipment is
      advanced from In Transit {">"} Notified {">"} Placed {">"} Released
      status. Alternatively, you can advance a record directly from the "Inboud
      Shipment" List view.
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Date Created: </strong>
          Date customer advised of Inbbound Shipment.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Time Notified: </strong>
          Time Railroad notified of car Arrival, car not Placed yet. Starts
          Demurrage clock countdown.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Time Placed: </strong>
          Time car is Constructively Placed in the Yard.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Time Released:</strong> Time car is Released to Railroad.
          Stops Demurrage clock countdown.
        </Typography>
      </li>
    </ul>
  </>
);

export const EditNoteLogHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Notes</strong>
    </Typography>
    <Typography variant="subtitle1">
      Use the Notes Tab to manually document key events and changes made to the
      Shipment record.
    </Typography>
    <ul>
      <li>
        <Typography>
          <AddIcon className={classes.icon} />
          <strong> ADD: </strong>Add new Note Line Item.
        </Typography>
      </li>
      <li>
        <Typography>
          <RemoveIcon className={classes.icon} />
          <strong> REMOVE: </strong>
          Remove selected Note Line Item - this info will be permanently lost.
        </Typography>
      </li>
    </ul>
  </>
);

export const CreateInboundSetupHelp = () => (
  <>
    <Typography variant="overline">
      <strong>Inbound Setup</strong>
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Account: </strong>
          Account must be selected first to auto-generate Lot Number and enable
          other fields.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Origin: </strong>
          Where the shipment is coming from. If shipped directly from customer,
          flip the "Copy Account" switch below the field.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Commodity: </strong>
          Select commodity category. Drives default units of measure and other
          settings.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Carrier Type: </strong>
          Select load type - If "Truckload", the "Railcar Number" field will
          change to "Trailer Number".
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Origin Carrier: </strong>
          Name of Railroad or Trucking company delivering the shipment.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Railcar / Trailer Number: </strong>
          Required if shipping by Rail.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>PO Number: </strong>
          Purchase Order Number provided by Customer. Used for auto-generating
          Lot Number.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Area and Zone: </strong>
          Select storage destination for inbound commodity. Location will be set
          at a later stage.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Lot Type: </strong> Used to auto-generate Lot Number.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Lot Number: </strong>
          Lot Number Prefix is auto-populated for selected Account.
          Auto-generated using either the PO Number for Car Lot loads, or
          current date for Perpetual loads. Lot Number, excluding the prefix,
          can be manually customized by selecting the "Manual Override" switch
          beneath the field.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>PO File: </strong>
          Attach the original PO from customer.
        </Typography>
      </li>
      <li>
        <Typography>
          <strong>Photos: </strong>
          Attach Photos if documenting a prior shipment an have available
          photos. Only used for past shipments where photos are available.
        </Typography>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Custom Fields</strong>
        </Typography>
        <Typography>
          User-defined in Account setup. These fields are Account-specific, so
          they may differ from one inbound to another.
        </Typography>
      </li>
    </ul>
  </>
);

export const CreateTallySheetHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Tally Sheet</strong>
    </Typography>
    <Typography variant="subtitle1">
      Completing the Tally Sheet Tab also creates new Inventory records, which
      will appear in the Inventory List once the Shipment is "Accepted" and
      "Stored".
    </Typography>
    <ul>
      <li>
        <Typography>
          <strong>Product Name: </strong>
          Provide full product name and description. Eg. "2.4.08 2&BTR WSPF 294
          PC" for 2x4x8 2&Better WSPF 294 Pieces. Items selected from Product
          Catalog will have an <InfoOutlinedIcon
            className={classes.icon}
          />{" "}
          Info Bubble in the right corner. Those not linked to a Product, will
          display <LinkOffIcon className={classes.icon} /> broken link icon.
        </Typography>
      </li>
      <li>
        <Typography gutterBottom>
          <strong>Qty. Advised: </strong>
          Quantity of product to be received - Unit count.
        </Typography>
      </li>
      <li>
        <Typography variant="overline">
          <strong>Custom Fields</strong>
        </Typography>
        <Typography>
          User-defined in Account setup or Product Catalog. These fields are
          Account- and Product-specific, so they may differ from one inbound to
          another. The Custom Fields button allows you to override default
          show/hide settings in the Account setup.
        </Typography>
      </li>
      <li>
        <Typography>
          <AddIcon className={classes.icon} />
          <strong> ADD: </strong>
          Add a custom Line Item to Tally Sheet - not linked to Product Catalog.
        </Typography>
      </li>
      <li>
        <Typography>
          <AddAllIcon className={classes.icon} />
          <strong> CATALOG ADD: </strong>
          Search Product Catalog to add a new Line Item to Tally Sheet.
        </Typography>
      </li>
      <li>
        <Typography>
          <RemoveIcon className={classes.icon} />
          <strong> REMOVE: </strong>
          Remove selected Tally Sheet Line Item.
        </Typography>
      </li>
      <li>
        <Typography gutterBottom>
          <strong>Total Quantity & Units: </strong>
          Sum total of Qty. Advised. Indicate units of measure for counting Qty
          Advised / Received.
        </Typography>
      </li>
      <li>
        <Typography gutterBottom>
          <strong>Bill Quantity & Units: </strong>
          Total Qty. Advised and Units used for invoicing customer. If Bill
          Units equal Weight Units, measured truck weight will be used for
          invoicing.
        </Typography>
      </li>
    </ul>
  </>
);

export const CreateNotesHelp = ({ classes }) => (
  <>
    <Typography variant="overline">
      <strong>Notes</strong>
    </Typography>
    <Typography variant="subtitle1">
      Use the Notes Tab to document key events and changes made to the Shipment
      record.
    </Typography>
    <ul>
      <li>
        <Typography>
          <AddIcon className={classes.icon} />
          <strong> ADD: </strong>Add new Note Line Item.
        </Typography>
      </li>
      <li>
        <Typography>
          <RemoveIcon className={classes.icon} />
          <strong> REMOVE: </strong>
          Remove selected Note Line Item - this info will be permanently lost.
        </Typography>
      </li>
    </ul>
  </>
);

const toSource = function (parent, child) {
  var str = "";

  for (var c in parent) {
    if (c === "obj") {
      str += toSource(parent[c]);
    } else if (!parent[c]) {
      str += (child ? `${child}.${c}: ` : `${c}: `) + '"" </br>';
    } else if (typeof parent[c] === "string") {
      str += (child ? `${child}.${c}: ` : `${c}: `) + parent[c] + " </br>";
    } else if (Array.isArray(parent[c])) {
      for (let i = 0; i < parent[c].length; i++) {
        const itm = parent[c][i];
        str += toSource(itm, `${c}[${i}]`);
      }
    } else {
      if (child) str += toSource(parent[c], `${child}.${c}`);
      else str += toSource(parent[c], c);
    }
  }

  return str;
};

export const DocData = (obj) => {
  const source = toSource(obj);
  const values = source.split("</br>");
  return (
    <>
      {" "}
      {values.map((val) => (
        <Typography> {val} </Typography>
      ))}
    </>
  );
};
